/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import styled from "styled-components";
import {
  Box,
  BrickLoader,
  ExtendedTooltip,
  FlexContainer,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";
import { Resource, ResourceUpdateStatus } from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { Separator } from "~/components/Styled";
import {
  useResourceComplianceStatusForUI,
  useGetResourceUpdateStatus,
} from "~/views/resources/hooks";
import { StatusLink } from "~/views/resources/ResourceUpdateStatus";
import { ComplianceStatusButton } from "~/views/resources/ResourceUpdateStatus/components/ComplianceStatusButton";
import {
  complianceInfoTooltip,
  updateStatusInfoTooltip,
} from "~/views/resources/ResourceUpdateStatus/const";
import { buildResourceUpdateStatusLink } from "~/views/resources/ResourceUpdateStatus/UpdatesModal/utils";

type Props = {
  resource?: Resource | null;
};

export function ResourceUpdateSnapshot({ resource }: Props) {
  const resourceIds = useMemo(
    () => (resource?.id ? [resource.id] : []),
    [resource?.id]
  );
  const { data: updateStatuses = [], loading: updateStatusesLoading } =
    useGetResourceUpdateStatus({
      resourceIds,
      skip: !resource?.id,
    });
  const { data: complianceStatuses = [], loading: complianceStatusesLoading } =
    useResourceComplianceStatusForUI({
      resourceIds,
      skip: !resource?.id,
    });

  const statusData = resource
    ? updateStatuses.find((item) => item.resourceId === resource.id)
    : undefined;
  const status =
    statusData?.updateStatus ?? ResourceUpdateStatus.NoPendingUpdates;

  const basedOn = statusData?.basedOn;
  const linkTo =
    basedOn && statusData
      ? buildResourceUpdateStatusLink({
          resourceId: statusData.resourceId,
          eventId: basedOn.eventId,
          eventActionId: basedOn.eventActionId,
          status,
        })
      : undefined;

  return (
    <>
      <StyledBox boxStyle="lightGrey">
        <FlexContainer
          alignItems="center"
          gap={theme.spacing.spacing02}
          marginBottom={theme.spacing.spacing02}
        >
          <Text tag="div" size="md" weight="medium">
            Last Update Status
          </Text>
          <ExtendedTooltip caption={updateStatusInfoTooltip}>
            <SVGIcon name="info" size="smd" css={{ display: "block" }} />
          </ExtendedTooltip>
        </FlexContainer>
        <LoaderWrap
          Component={() => <BrickLoader height="2rem" />}
          loading={updateStatusesLoading}
        >
          <StatusLink
            status={status}
            count={statusData?.pendingUpdateCount}
            to={linkTo}
          />
        </LoaderWrap>
        <FullWidthSeparator />
        <FlexContainer
          alignItems="center"
          gap={theme.spacing.spacing02}
          marginBottom={theme.spacing.spacing02}
        >
          <Text tag="div" size="md" weight="medium">
            Compliance
          </Text>
          <ExtendedTooltip caption={complianceInfoTooltip}>
            <SVGIcon name="info" size="smd" css={{ display: "block" }} />
          </ExtendedTooltip>
        </FlexContainer>
        {resource ? (
          <ComplianceStatusButton
            complianceStatusesLoading={complianceStatusesLoading}
            complianceStatuses={complianceStatuses}
            resource={resource}
          />
        ) : null}
      </StyledBox>
    </>
  );
}

const StyledBox = styled(Box)`
  flex: 1;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
`;

const FullWidthSeparator = styled(Separator)`
  width: calc(100% + ${theme.spacing.spacing03} * 2);
  margin-left: -${theme.spacing.spacing03};
  margin-right: -${theme.spacing.spacing03};
  margin-top: ${theme.spacing.spacing04};
  margin-bottom: ${theme.spacing.spacing03};
`;
